(() => {
  const runtimeKey = '__deblancasCloudflareStreamRuntime';
  const existingRuntime = window[runtimeKey];
  if (existingRuntime) {
    existingRuntime.refresh();
    return;
  }

  let nativeThumbnailsScheduled = false;
  let nativeThumbnailsReady = false;

  const motionAllowsPlayback = () => !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isThemeEditor = () => Boolean(window.Shopify?.designMode);

  const attachBestSource = (video) => {
    if (!video || video.dataset.streamSourceReady === 'true') return Boolean(video?.src);
    const canPlayHls = Boolean(video.canPlayType?.('application/vnd.apple.mpegurl'));
    const source = canPlayHls ? video.dataset.hlsSrc : video.dataset.mp4Src;
    if (!source) return false;
    video.src = source;
    video.dataset.streamSourceType = canPlayHls ? 'hls' : 'mp4';
    video.dataset.streamSourceReady = 'true';
    video.load();
    return true;
  };

  const attachMp4Source = (video) => {
    if (!video || video.dataset.streamSourceReady === 'true') return Boolean(video?.src);
    const source = video.dataset.mp4Src;
    if (!source) return false;
    video.src = source;
    video.dataset.streamSourceType = 'mp4';
    video.dataset.streamSourceReady = 'true';
    video.load();
    return true;
  };

  const fallbackToMp4 = (video) => {
    if (!video || video.dataset.streamSourceType !== 'hls' || video.dataset.streamMp4Fallback === 'true') return false;
    if (!video.dataset.mp4Src) return false;
    video.dataset.streamMp4Fallback = 'true';
    video.dataset.streamSourceType = 'mp4';
    video.src = video.dataset.mp4Src;
    video.load();
    return true;
  };

  const loadNativeThumbnails = () => {
    if (!motionAllowsPlayback() || isThemeEditor()) return;

    document.querySelectorAll('[data-stream-native-thumbnail]').forEach((thumbnail) => {
      thumbnail.preload = 'auto';
      thumbnail.muted = true;
      thumbnail.loop = true;
      thumbnail.playsInline = true;
      if (!attachMp4Source(thumbnail)) return;
      const playback = thumbnail.play();
      playback?.catch(() => {});
    });
  };

  const scheduleNativeThumbnails = () => {
    if (isThemeEditor()) return;
    if (nativeThumbnailsReady) {
      loadNativeThumbnails();
      return;
    }
    if (nativeThumbnailsScheduled) return;
    nativeThumbnailsScheduled = true;

    let completed = false;
    let lcpObserver;
    let signalTimer;
    let deadlineTimer;
    const load = () => {
      if (completed) return;
      completed = true;
      window.clearTimeout(signalTimer);
      window.clearTimeout(deadlineTimer);
      lcpObserver?.disconnect();
      nativeThumbnailsReady = true;
      loadNativeThumbnails();
    };
    const scheduleFromRenderSignal = () => {
      if (completed || signalTimer) return;
      signalTimer = window.setTimeout(load, 650);
    };

    const elapsedSinceNavigation = window.performance?.now?.() || 0;
    deadlineTimer = window.setTimeout(load, Math.max(0, 6000 - elapsedSinceNavigation));

    if ('PerformanceObserver' in window) {
      try {
        lcpObserver = new PerformanceObserver((entries) => {
          if (!entries.getEntries().length) return;
          scheduleFromRenderSignal();
        });
        lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });
      } catch (_) {}
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', scheduleFromRenderSignal, { once: true });
    } else {
      scheduleFromRenderSignal();
    }
  };

  class CloudflareStreamVideo extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.slide = this.closest('[data-stream-slide]');
      this.gallery = this.closest('product-slider, product-slider-thumbnails');
      this.video = this.querySelector('[data-stream-main-video]');
      this.playButton = this.querySelector('.cloudflare-stream__play');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.autoplay = this.dataset.autoplay === 'true';
      this.primePlayer = this.dataset.primePlayer === 'true';
      this.themeEditor = isThemeEditor();
      this.milestones = new Set();
      this.eventController = new AbortController();

      if (!this.video) return;
      this.video.muted = true;
      this.video.loop = this.dataset.loop === 'true';
      this.video.controls = this.dataset.controls === 'true';
      this.video.playsInline = true;
      if (this.primePlayer && !this.themeEditor) attachBestSource(this.video);

      scheduleNativeThumbnails();
      this.bindPlayerEvents();
      this.playButton?.addEventListener('click', () => this.activate(true), { signal: this.eventController.signal });
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.pause();
      }, { signal: this.eventController.signal });

      this.setupLazyLoading();
      this.setupGalleryTracking();

      requestAnimationFrame(() => {
        if (this.slide?.classList.contains('is-selected')) this.activate(false);
      });
    }

    disconnectedCallback() {
      this.eventController?.abort();
      this.lazyObserver?.disconnect();
      this.visibilityObserver?.disconnect();
      this.slideObserver?.disconnect();
      if (this.idleCallback && 'cancelIdleCallback' in window) cancelIdleCallback(this.idleCallback);
      if (this.idleTimeout) clearTimeout(this.idleTimeout);
      clearTimeout(this.primeTimer);
      if (this.flickityChangeHandler && this.gallery?.flkty?.off) {
        this.gallery.flkty.off('change', this.flickityChangeHandler);
      }
      this.pause();
    }

    bindPlayerEvents() {
      this.video.addEventListener('canplay', () => {
        this.dataset.playerReady = 'true';
      }, { signal: this.eventController.signal });
      this.video.addEventListener('playing', () => this.handlePlaying(), { signal: this.eventController.signal });
      this.video.addEventListener('waiting', () => {
        if (!this.isPriming && this.wantsPlayback) this.dataset.buffering = 'true';
      }, { signal: this.eventController.signal });
      this.video.addEventListener('pause', () => {
        if (!this.isPriming) this.dataset.playing = 'false';
      }, { signal: this.eventController.signal });
      this.video.addEventListener('ended', () => this.emitOnce('complete'), { signal: this.eventController.signal });
      this.video.addEventListener('error', () => {
        if (fallbackToMp4(this.video)) {
          if (this.isPriming || this.wantsPlayback) this.video.play()?.catch(() => {});
          return;
        }
        this.dataset.playing = 'false';
        this.dataset.playerError = 'true';
      }, { signal: this.eventController.signal });
      this.video.addEventListener('timeupdate', () => this.trackProgress(), { signal: this.eventController.signal });
    }

    setupLazyLoading() {
      this.lazyObserver = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        if (this.primePlayer) this.schedulePlayerPrime();
        this.lazyObserver.disconnect();
      }, { rootMargin: '600px 0px', threshold: 0.01 });
      this.lazyObserver.observe(this.gallery || this);
    }

    schedulePlayerPrime() {
      if (this.primeScheduled || this.reducedMotion.matches) return;
      this.primeScheduled = true;
      const prime = () => this.primeVideo();
      if ('requestIdleCallback' in window) {
        this.idleCallback = requestIdleCallback(prime, { timeout: 1200 });
      } else {
        this.idleTimeout = setTimeout(prime, 450);
      }
    }

    primeVideo() {
      if (this.wantsPlayback || document.hidden || this.dataset.playerError === 'true') return;
      if (!attachBestSource(this.video)) return;
      this.video.preload = 'auto';
      this.isPriming = true;
      const playback = this.video.play();
      playback?.catch(() => {
        this.isPriming = false;
      });
    }

    handlePlaying() {
      this.dataset.playerReady = 'true';
      delete this.dataset.buffering;
      if (this.isPriming && !this.wantsPlayback) {
        clearTimeout(this.primeTimer);
        this.primeTimer = setTimeout(() => {
          if (this.wantsPlayback) return;
          this.video.pause();
          if (this.video.buffered.length && this.video.buffered.end(0) > 0.5) this.video.currentTime = 0;
          this.isPriming = false;
        }, 650);
        return;
      }
      if (!this.wantsPlayback || document.hidden) {
        this.video.pause();
        this.dataset.playing = 'false';
        return;
      }
      clearTimeout(this.primeTimer);
      this.isPriming = false;
      this.dataset.playing = 'true';
      this.emitOnce('play');
    }

    setupGalleryTracking() {
      this.gallery?.addEventListener('slideChanged', (event) => {
        if (event.detail?.currentElement === this.slide) this.activate(false);
        else this.pause();
      }, { signal: this.eventController.signal });

      if (this.gallery?.matches('product-slider-thumbnails') && this.gallery.flkty?.on) {
        this.flickityChangeHandler = (index) => {
          const activeSlide = this.gallery.flkty.cells[index]?.element;
          if (activeSlide === this.slide) this.activate(false);
          else this.pause();
        };
        this.gallery.flkty.on('change', this.flickityChangeHandler);
      }

      this.slideObserver = new MutationObserver(() => {
        if (this.slide.classList.contains('is-selected')) this.activate(false);
        else if (this.gallery?.matches('product-slider-thumbnails')) this.pause();
      });
      this.slideObserver.observe(this.slide, { attributes: true, attributeFilter: ['class'] });

      if (!this.themeEditor) {
        this.visibilityObserver = new IntersectionObserver((entries) => {
          const entry = entries[0];
          if (entry.intersectionRatio >= 0.5) this.emitOnce('impression');
          if (entry.intersectionRatio >= 0.65) this.activate(false);
          else this.pause();
        }, { threshold: [0, 0.65, 1] });
        this.visibilityObserver.observe(this);
      }
    }

    async activate(userInitiated) {
      this.pauseSiblings();
      const shouldPlay = userInitiated || (this.autoplay && !this.reducedMotion.matches && !this.themeEditor);
      this.wantsPlayback = shouldPlay;
      if (!shouldPlay || this.dataset.playerError === 'true') {
        this.pause();
        return;
      }

      clearTimeout(this.primeTimer);
      this.isPriming = false;
      if (!attachBestSource(this.video)) return;
      this.video.preload = 'auto';
      try {
        await this.video.play();
      } catch (_) {
        this.dataset.playing = 'false';
      }
    }

    pauseSiblings() {
      const scope = this.gallery || this.closest('.product-image-container');
      scope?.querySelectorAll('cloudflare-stream-video').forEach((video) => {
        if (video !== this) video.pause();
      });
      scope?.querySelectorAll('.product-single__media-native-video video').forEach((video) => video.pause());
      scope?.querySelectorAll('.product-single__media-external-video').forEach((media) => {
        const iframe = media.querySelector('iframe');
        if (!iframe?.contentWindow) return;
        if (media.dataset.provider === 'youtube') {
          iframe.contentWindow.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo', args: '' }), '*');
        } else if (media.dataset.provider === 'vimeo') {
          iframe.contentWindow.postMessage(JSON.stringify({ method: 'pause' }), '*');
        }
      });
    }

    pause() {
      this.wantsPlayback = false;
      clearTimeout(this.primeTimer);
      this.video?.pause();
      this.isPriming = false;
      this.dataset.playing = 'false';
    }

    trackProgress() {
      if (!this.video?.duration) return;
      const progress = this.video.currentTime / this.video.duration;
      if (progress >= 0.25) this.emitOnce('25-percent');
      if (progress >= 0.5) this.emitOnce('50-percent');
      if (progress >= 0.75) this.emitOnce('75-percent');
    }

    emitOnce(name) {
      if (this.milestones.has(name)) return;
      this.milestones.add(name);
      document.dispatchEvent(new CustomEvent(`deblancas:stream-video-${name}`, {
        detail: {
          productId: this.dataset.productId,
          productHandle: this.dataset.productHandle,
          videoUid: this.dataset.videoUid,
          videoPosition: Number(this.dataset.videoPosition || 0)
        }
      }));
    }
  }

  if (!customElements.get('cloudflare-stream-video')) {
    customElements.define('cloudflare-stream-video', CloudflareStreamVideo);
  }

  window[runtimeKey] = {
    refresh: scheduleNativeThumbnails
  };

  scheduleNativeThumbnails();
})();
